import type {
  Optional,
  Resume,
  UserDocumentTemplate,
  ResumeAvatarTransform,
  DateRangePickerValue,
  EnumerableResumeSectionType,
  UserDocumentTemplateId,
  ResumeExample,
  SingleTemplateSettings,
} from '@rio/types'
import { Step as JoyRideStep } from 'react-joyride'
import { TABS } from 'builder/components/FillResumeModal/constants'
import {
  AIResumeLinkedInSteps,
  AIResumeFlows,
  FetchStatuses,
  ResumeUploadStep,
  ResumeUploadStatus,
  ResumeValidationStatus,
  GenerateResumeStatus,
  SpeechToTextStatus,
  AIResumeWarningModalActions,
  AIResumeWarningModalTypes,
  VoiceInputErrors,
  GenerateAiSummaryStatus,
  AiSummaryGenerateType,
  FORMATS,
} from 'builder/modules/constants'
import { SelectValue } from 'builder/components/Select/types'
import { JoyRideButtonProps } from 'builder/components/JoyRide'
import { EditorPanelType, LanguageLevelData, SkillLevelData } from '../generalEditor'
import { DownloadTypes } from '../rendering'
import { SectionNames } from './sectionNames'

export enum PrefillStatuses {
  /** initial value */
  notAsked = 'notAsked',
  /** resume doesn't need to prefill */
  skipped = 'skipped',
  /** resume is prefilling */
  scheduled = 'scheduled',
  /** resume prefilled and ready to merge */
  readyToMerge = 'readyToMerge',
  /** when accept of merge was failed */
  mergeFailed = 'mergeFailed',
  /** when reject of merge was failed */
  rejectFailed = 'rejectFailed',
  /** when resume was not be changed and was merged automatically */
  mergedAuto = 'mergedAuto',

  error = 'error',
  failed = 'failed',
  done = 'done',
  rejected = 'rejected',
  merged = 'merged',
}

export enum PrefillProviders {
  topresume = 'topresume',
}

export type ResumeScoreAPIData = {
  jobPostingId: number
  employerName: string
  suggestedJobTitle: string
  averageScore: number
  keywords: string[]
}

export type JobPostingAPIData = {
  jobPostingLink: string
  jobPostingId: number
  isManagementJob: boolean
  employerName: Optional<string>
  recommendedJobTitle: Optional<string>
  visibleJobTitle: string
  recommendedSkills: string[]
  visibleSkills: string[]
  resumeScore: number
  apiScore: number
  averageScore: number
  keywords: string[]
  recommendedKeywords: string[]
}

export type JobPostingManualInputValues = {
  companyName: string
  jobTitle: string
  role: string
}

export type ResumeOptimizerSection = {
  jobPostingLinkForModal: string
  fetchScoreStatus: FetchStatuses
  saveJobPostingDataStatus: FetchStatuses
  fetchJobPostingDataStatus: FetchStatuses
  deleteJobPostingStatus: FetchStatuses
  editJobDetailsStatus: FetchStatuses
  showManualInputScreen: boolean
  temporaryJobPostingAPIData: JobPostingAPIData
  resumeScoreAPIData: ResumeScoreAPIData
  jobPostingAPIData: JobPostingAPIData
  manualInput: JobPostingManualInputValues
  bannerTriggeredFrom: OptimizerBannerTriggerSource | null
}

export type PrefillResumeSection = {
  resumeValidationStatus: ResumeValidationStatus
  selectedResume: File | null
  isSavingPrefilledResume: boolean
  validationError: string | null
  resumeUploadStatus: ResumeUploadStatus
  error: Error | string | null
  errorSource: ResumeUploadStep | null
  successStatus: boolean
}

export type AICoverLetter = {
  isEditJobDetailsFocused: boolean
  isClickedOnAiCVEditJobDetails: boolean
  isClickedOnAiCVEditModalIcon: boolean
  isOpenAICVOptimizerModal: boolean
  isShowUntailoreView: boolean
  generateAICoverLetterStatus: FetchStatuses
  aICVEditedJobTitle: string
  aICVEditedCompanyName: string
}

export type AISummary = {
  isModalOpen: boolean
  isResultModalOpen: boolean
  isWithTailoredResumeModalOpen: boolean
  generateAiSummaryStatus: GenerateAiSummaryStatus
  summary: string | null
  actionType: AiSummaryGenerateType | null
  shouldAnimateResultPopupContent: boolean
  shouldAnimateEditorFieldContent: boolean
  includeJobPosting: boolean
}

export type AIResume = {
  showAIResumeModal: boolean
  aiResumeFlow: AIResumeFlows
  linkedInData: LinkedInData
  questionnaireData: QuestionnaireData
  generateResumeStatus: GenerateResumeStatus
  showWarningModal: boolean
  warningModalType: AIResumeWarningModalTypes | null
  warningModalAction: AIResumeWarningModalActions | null
}

export enum CreateResumeWithLinkedInSteps {
  linkedInURL = 'linkedInURL',
  linkedInProfile = 'linkedInProfile',
}

export type CreateResumeWithLinkedInData = {
  fullName: string | null
  position: string | null
  avatar: string | null
}

export type CreateResumeWithLinkedIn = {
  showCreateResumeWithLinkedInModal: boolean
  currentStep: CreateResumeWithLinkedInSteps
  fetchLinkedInProfileStatus: FetchStatuses
  linkedInProfileURL: string
  linkedInProfileData: CreateResumeWithLinkedInData | null
  errorState: boolean
  updateResumeWithLinkedInStatus: FetchStatuses
}

export type StepByStepFlow = {
  shouldRedirectToAdditionalStep: boolean
}

export type LinkedInProfileData = {
  fullName: string | null
  position: string | null
  avatar: string | null
}

export type LinkedInData = {
  currentStep: AIResumeLinkedInSteps
  linkedInProfileURL: string
  fetchingLinkedInProfile: boolean
  linkedInProfileData: LinkedInProfileData
}

export type QuestionnaireData = {
  step: number
  progress: number
  accomplishmentsVoiceProcessingStatus: SpeechToTextStatus
  accomplishmentsVoiceInputError: VoiceInputErrors | null
  activeAccomplishmentsTab: TABS
  accomplishmentsAudio: Blob[]
  accomplishmentsAudioDuration: number
  goalsVoiceProcessingStatus: SpeechToTextStatus
  goalsVoiceInputError: VoiceInputErrors | null
  activeGoalsTab: TABS
  goalsAudio: Blob[]
  goalsAudioDuration: number
  userInput: {
    workExperience: {
      cards: AIResumeWorkExpCardData[]
      confirmDeleteCardId: string
    }
    desiredPosition: string
    education: {
      cards: AIResumeEducationCardData[]
    }
    currentSkills: string[]
    suggestedSkillsSource: string[]
    suggestedSkills: string[]
    pastAccomplishments: string
    futureGoals: string
  }
}

export type AIResumeWorkExpCardDataError = {
  position: boolean
  company: boolean
  dateFrom: boolean
  dateUntil: boolean
}

export type AIResumeWorkExpCardData = {
  id: string
  position: string
  company: string
  date: DateRangePickerValue
  errors?: AIResumeWorkExpCardDataError
  isCollapsed: boolean
}

export type AIResumeEducationCardData = {
  id: string
  institute: string
  degree: string
  date: DateRangePickerValue
  isCollapsed: boolean
}

export interface ResumeEventTrackedSectionsType {
  [resumeId: number]: string[]
}

export type OpenedSectionsMap = {
  [key in SectionNames]?: boolean
} & {
  [key in ResumeSectionId]?: boolean
}

export type AiResumeReview = {
  fetchStatus: FetchStatuses
  reviewResult: AiResumeReviewResult | null
  isAiResumeReviewPaywallModalOpen: boolean
}

export type ResumeEditorStore = {
  isLoading: boolean
  isSyncing: boolean
  isUploadingPhoto: boolean
  resume: Resume | null
  levels: {
    language: LanguageLevelData[]
    skill: SkillLevelData[]
  } | null
  templates: UserDocumentTemplate[]
  error: Error | null
  openedCard: ResumeOpenedCard | null
  openedSections: OpenedSectionsMap
  prefill: {
    status: PrefillStatuses
    provider: PrefillProviders
  }
  isOptimizerModalOpen: boolean
  editJobPosting: boolean
  openedAISuggestionsPopup: Optional<AvailableAISuggestions>
  optimizerSection: ResumeOptimizerSection
  prefillResumeSection: PrefillResumeSection
  isEditJobDetails: boolean
  showToolTip: boolean
  companyName: string
  jobTitle: string
  isClickedOnDeleteJobPosting: boolean
  isClickedOnEditJobDetails: boolean
  changeJobPostingLink: boolean
  isFromJobTracker: boolean
  isOpenSavingInfoBanner: boolean
  isClickedOnAiCVBanner: boolean
  AICoverLetter: AICoverLetter
  aiResume: AIResume
  viewGenerateCLBannerEventTracked: boolean
  useExample: UseExample
  viewTailoringBannerEventTracked: boolean
  aiProfile: AISummary
  highlightedElementData: HighlightedElementData | null
  openedKeywordsModalType: SectionNames | null
  improveResumePanel: {
    isOpen: boolean
    isBadgeAnimating: boolean
  }
  autoTailoring: {
    fetchStatus: FetchStatuses
    openedModal: AutoTailoringOpenedModalType | null
    isApproveModalOpen: boolean
    autoTailoredResume: Resume | null
    jobPostingData: ResumeScoreAPIData | null
    keywordsMap: Record<string, boolean>
    autoTailoredResumeApproveStatus: FetchStatuses
    isFeaturingAutoTailorModalOpenOnDownload: boolean
  }
  autoTailoringFromPublicJobBoard: AutoTailoringFromPublicJobBoard
  originalResume: Resume | null
  resumeEventTrackedSections: ResumeEventTrackedSectionsType
  resumeDownloadData: DownloadTypes
  isPageScrolled: boolean
  aiWorkExperience: AISummary
  aiGenerationPaywallType: AiGenerationPaywallType | null
  createResumeWithLinkedIn: CreateResumeWithLinkedIn
  stepByStepFlow: StepByStepFlow
  editorPanelType: EditorPanelType
  aiResumeReview: AiResumeReview
}

export interface AvatarUploadSimplePayload {
  file: File
}

export interface AvatarUploadEditorPayload {
  file?: Optional<File>
  processedFile: File
  transform: ResumeAvatarTransform
}

export type AvatarUploadPayload = AvatarUploadSimplePayload | AvatarUploadEditorPayload

export interface VoiceInputTabProps {
  audioChunks: Array<Blob>
  duration: number
  errorWhileRecording: string
  isRecording: boolean
  onClearRecording: () => void
  onStopRecording: () => void
}

export type HighlightedElementData = Pick<
  JoyRideStep,
  'title' | 'content' | 'disableScrollParentFix' | 'styles' | 'placement'
> & {
  target: string
  buttonLabel?: string
  showSkipOnLast?: boolean
  skipButtonLabel?: string
  disableOverlayClose?: boolean
  disableCloseOnEsc?: boolean
  onSkip?: () => void
  onClose?: () => void
  nextButtonProps?: JoyRideButtonProps
  skipButtonProps?: JoyRideButtonProps
}

export enum AvailableAISuggestions {
  jobTitle = 'jobTitle',
  skills = 'skills',
}

export type AutoTailoringOpenedModalType =
  | 'keywords_selection'
  | 'auto_tailoring'
  | 'tailored_resume_overview'

export type ResumeOpenedCard = {
  id: Optional<number | string>
  sectionId: ResumeSectionId
}

export enum TemplateCategories {
  all = 'all',
  simple = 'simple',
  ats = 'ats',
  professional = 'professional',
  creative = 'creative',
  modern = 'modern',
  free = 'free',
}

export type TemplateCategory = {
  canonical_resource_id: number | null
  content: string | null
  content_html: string | null
  content_subtitle: string | null
  content_title: string | null
  country_id: number | null
  created_at: string | null
  display_name: string | null
  icon: string | null
  menu_description: string | null
  meta_description: string | null
  meta_title: string | null
  navbar_presence: boolean
  popular_category: boolean
  slug: string | null
  status: string | null
  subtitle: string | null
  template_names: string[]
  title: string | null
  type: string | null
  updated_at: string | null
}

export type OptimizerBannerTriggerSource = 'profile' | 'workExperiences'

export type ResumeSectionId = EnumerableResumeSectionType | 'accomplishments' | number

export type UpdateSimpleFieldFunc = (field: any, value: any, debounce?: boolean) => void

export type AiGenerationPaywallType = 'cover_letter' | 'profile' | 'work_experience'

export type TemplateChangeHandler = (id: UserDocumentTemplateId) => void

export type FontChangeHandler = (params: {
  value: SelectValue
  newFontType: 'primaryFont' | 'secondaryFont' | 'tertiaryFont'
  selectedPrimaryFont: SelectValue
  selectedSecondaryFont: SelectValue
  selectedTertiaryFont: SelectValue
}) => void

export type SizeChangeHandler = (newSize: number, oldSize: number) => void

export type SpacingChangeHandler = (newSize: number, oldSize: number) => void

export type LayoutFormatChangeHandler = (value: SelectValue) => void

export type SettingsFieldChangeHandler<
  K extends keyof SingleTemplateSettings = keyof SingleTemplateSettings,
  V extends SingleTemplateSettings[K] = SingleTemplateSettings[K],
> = (settingsName: K, value: V, prevValue: V) => void

export type ColorChangeHandler = (color: Optional<string>) => void

export type DownloadDocumentHandler = (params?: { format?: FORMATS }) => void

export type UpdateTemplateSettingsParams<S extends keyof SingleTemplateSettings> = {
  settingsName: S
  value: SingleTemplateSettings[S]
}

export type AiResumeReviewResult = {
  scoreContent: string
  scoreHeadline: number
  topStrengths: string
  priorityFixes: string
  quickTips: string
}

export type CreateJobPostingResponse = {
  jobPosting: {
    id: number
    jobTitle: string
    companyName: string
    keywords: string[]
  }
}

export type FetchScoreResponse = {
  averageScore: number
}

export enum AutoTailoringFromPublicJobBoardStep {
  step1 = 'step1',
  step2 = 'step2',
  finished = 'finished',
}

export interface AutoTailoringFromPublicJobBoard {
  enabled: boolean
  mode: AutoTailoringMode
  step: AutoTailoringFromPublicJobBoardStep
  processingAutoTailoring: FetchStatuses
  jobCardId: number | null
  jobPostingId: number | null
  showPrefillBanner: boolean
}

export enum AutoTailoringMode {
  fromPublicJB = 'auto_tailoring_from_public_job_board',
  fromMemberJB = 'auto_tailoring_from_member_job_board',
}

export type PossibleAutoTailoringUrlParams = {
  mode: AutoTailoringMode
  jobCardId?: Optional<number>
  resumeId?: Optional<number>
  jobPostingId?: Optional<number>
  autotailoringStep?: Optional<AutoTailoringFromPublicJobBoardStep>
  approveAutoTailoredResume?: Optional<boolean>
  showSelfTailorModal?: Optional<boolean>
  preventUrlParamsCleanup?: Optional<boolean>
  showResumeTailoredBanner?: Optional<boolean>
  downloadDocumentFormat?: Optional<FORMATS>
}

export type BriefExampleCategory = {
  id: number
  name: string
}

export type UseExample = {
  showUseExampleModal: boolean
  resumeExamples: ResumeExample[]
  fetchingResumeExamples: boolean
  currentPage: number
  totalPages: number
  categories: BriefExampleCategory[]
  jobTitles: string[]
  selectedCategoryId: number
  searchedTitle: string
}
